/**
 * Logger Utility
 *
 * Centralized logging with different log levels
 * Supports structured logging and can be extended for telemetry
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext | undefined;
}

/**
 * Determine if we should log based on level and environment
 */
function shouldLog(level: LogLevel): boolean {
  // In production, only log warnings and errors
  if (import.meta.env.PROD) {
    return level === 'warn' || level === 'error';
  }
  return true;
}

/**
 * Format log entry for console output
 */
function formatLogEntry(entry: LogEntry): string {
  const { timestamp, level, message, context } = entry;
  const contextStr = context ? ` ${JSON.stringify(context)}` : '';
  return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
}

/**
 * Create a log entry
 */
function createLogEntry(
  level: LogLevel,
  message: string,
  context?: LogContext
): LogEntry {
  return {
    timestamp: new Date().toISOString(),
    level,
    message,
    context,
  };
}

/**
 * Logger singleton
 */
export const logger = {
  /**
   * Debug level log - only shown in development
   */
  debug(message: string, context?: LogContext): void {
    if (!shouldLog('debug')) return;

    const entry = createLogEntry('debug', message, context);
    console.debug(formatLogEntry(entry));
  },

  /**
   * Info level log - general information
   */
  info(message: string, context?: LogContext): void {
    if (!shouldLog('info')) return;

    const entry = createLogEntry('info', message, context);
    console.info(formatLogEntry(entry));
  },

  /**
   * Warning level log - potential issues
   */
  warn(message: string, context?: LogContext): void {
    if (!shouldLog('warn')) return;

    const entry = createLogEntry('warn', message, context);
    console.warn(formatLogEntry(entry));
  },

  /**
   * Error level log - errors and exceptions
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (!shouldLog('error')) return;

    const errorContext: LogContext = { ...context };

    if (error instanceof Error) {
      errorContext.errorMessage = error.message;
      errorContext.errorStack = error.stack;
    } else if (error !== undefined) {
      errorContext.error = String(error);
    }

    const entry = createLogEntry('error', message, errorContext);
    console.error(formatLogEntry(entry));
  },

  /**
   * Log a user action (for analytics)
   */
  action(action: string, details?: LogContext): void {
    this.info(`User action: ${action}`, { action, ...details });
  },

  /**
   * Log a performance metric
   */
  metric(name: string, value: number, unit: string = 'ms'): void {
    this.debug(`Metric: ${name}`, { name, value, unit });
  },

  /**
   * Create a timer for measuring duration
   */
  timer(name: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.metric(name, Math.round(duration));
    };
  },
};

/**
 * Log uncaught errors globally
 */
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    logger.error('Uncaught error', event.error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', event.reason);
  });
}
